/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { nativeIssueTableEntryPoint } from '@jira/issue-table__issue-table/src/ui/nativeIssueTableEntryPoint.entrypoint.tsx';
import timelineParameters from '@jira/timeline__timeline-view/src/__generated__/JiraTimelineQuery$parameters';
import filterRefinementParameters from '@jira/timeline__filter-refinement/src/__generated__/FilterRefinementTimelineQuery$parameters';
import type { JiraJqlScopeInput } from '@jira/timeline__filter-refinement/src/__generated__/FilterRefinementTimelineQuery.graphql.ts';
import { fg } from '@atlassian/jira-feature-gating';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { JiraSoftwareBoardAri } from '@atlassian/ari/jira-software/board';
import softwareTimelineEmbedParameters from './__generated__/JiraSoftwareHarmonisedTimelineEmbedQuery$parameters.ts';
import type {
	Queries,
	ExtraProps,
	PreloadedEntryPoints,
} from './softwareTimelineEmbedEntrypoint.entrypoint.types.tsx';

type SoftwareTimelineEmbedEntrypointComponent = EntryPointComponent<
	Queries,
	{},
	PreloadedEntryPoints,
	ExtraProps
>;

const NAMESPACE = 'TIMELINE';
const TOGGLEABLE_FIELD_SET_IDS = ['assignee', 'status'];

/**
 * The window used to filter sprints shown on the timeline: from one year before
 * the current date to two years after it. Bounds are ISO date-time strings
 * matching the `JiraDateTimeWindow` input.
 */
const getSprintsActiveWithin = (): { start: string; end: string } => {
	const now = new Date();

	const start = new Date(now);
	start.setFullYear(start.getFullYear() - 1);

	const end = new Date(now);
	end.setFullYear(end.getFullYear() + 2);

	return { start: start.toISOString(), end: end.toISOString() };
};

export const softwareTimelineEmbedEntrypoint: EntryPoint<
	SoftwareTimelineEmbedEntrypointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<SoftwareTimelineEmbedEntrypointComponent>(
		/* webpackChunkName: "async-software-timeline-embed" */ './JiraSoftwareHarmonisedTimelineEmbed.tsx',
	),
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		const {
			context: { match, route },
			tenantContext: { cloudId },
		} = entryPointParams;
		const { projectKey: rawProjectKey, boardId, viewId } = match.params;
		const projectKey = rawProjectKey ?? '';

		const sourceARI = JiraSoftwareBoardAri.create({
			siteId: cloudId,
			boardId: String(boardId),
		}).toString();

		const isCMP = route.name === 'classic-software-timeline-embed';

		const issueSearchInput = {
			customInput: {
				jiraTimelineInput: {
					boardId: String(boardId),
					useSavedFilters: true,
				},
			},
		};

		const today = Date.now();
		const itemId = expValEquals('jira_sw_timeline_saved_views', 'isEnabled', true, false)
			? `timeline${viewId ? `/${viewId}` : ''}`
			: 'timeline';
		const jiraViewQueryInput = {
			boardIdAndItemQuery: {
				cloudId,
				itemId,
				boardId: Number(boardId),
			},
		};

		const filterRefinementScope: JiraJqlScopeInput = {
			backlog: {
				boardId: Number(boardId),
			},
		};

		return {
			extraProps: {
				today,
				isTimelineHarmonisationM2Enabled: expValEquals(
					'timeline_harmonisation_m2',
					'isEnabled',
					true,
					false,
				),
				isTimelineGroupByEnabled: false,
				isBulkOperationsInTimelineEnabled: expValEquals(
					'bulk_operations_in_timeline',
					'isEnabled',
					true,
					false,
				),
				jqlParam: '',
				...(fg('timeline-filter-refinement-technical-changes')
					? { filterRefinementScope, includeBoardFilters: true }
					: {}),
			},
			queries: {
				softwareTimelineQuery: {
					options: {
						fetchPolicy: fg('jira_software_timeline_perf_improvements')
							? ('store-or-network' as const)
							: ('store-and-network' as const),
					},
					parameters: softwareTimelineEmbedParameters,
					variables: {
						cloudId,
						projectKey,
						boardId: sourceARI,
						isCMP,
					},
				},
				timelineQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters: timelineParameters,
					variables: {
						boardIds: [sourceARI],
						amountOfColumns: 500,
						cloudId,
						...(expValEquals('timeline_harmonisation_m2', 'isEnabled', true, false)
							? {
									fieldSetIds: undefined,
									fieldSetsInput: {
										viewInput: {
											namespace: NAMESPACE,
											viewId: 'timeline_default',
										},
									},
								}
							: {
									fieldSetIds: [],
									fieldSetsInput: { fieldSetIds: [] },
								}),
						filterId: null,
						first: 50,
						issueSearchInput,
						namespace: NAMESPACE,
						projectKey,
						settings: {
							groupBy: null,
							hideDone: false,
							hierarchyEnabled: true,
							jql: null,
						},
						shouldQueryFieldSetsById: false,
						shouldQueryHasChildren: !fg('jsc_list_expand_all'),
						shouldQueryHasChildrenWithCount: fg('jsc_list_expand_all'),
						shouldQueryHasParent: fg('jira_harmonised_timeline_hierarchy_flyout'),
						scope: expValEquals('timeline_harmonisation_m2', 'isEnabled', true, false)
							? {
									operationScope: 'TIMELINE_PROJECT' as const,
									projectKey,
								}
							: undefined,
						staticViewInput: {
							isGroupingEnabled: false,
							isHideDoneEnabled: false,
							isHierarchyEnabled: true,
						},
						viewConfigInput: {
							staticViewInput: {
								isGroupingEnabled: false,
								isHideDoneEnabled: false,
								isHierarchyEnabled: true,
							},
						},
						jiraViewQueryInput,
						// jiraViewQueryInputForIssues is temporary and is used to replace jiraViewQueryInput.
						// It will be removed when jira_timeline_saved_view_technical is cleaned up.
						// When cleaning up jira_timeline_saved_view_technical, replace this with jiraViewQueryInput.
						// Keep this consistent with the business project.
						jiraViewQueryInputForIssues: expValEquals(
							'jira_sw_timeline_saved_views',
							'isEnabled',
							true,
							false,
						)
							? jiraViewQueryInput
							: null,
						timelineViewSettings: {},
						timelineStaticViewInput: {},
						viewId: 'timeline_default',
						...(expValEquals('timeline_harmonisation_m2', 'isEnabled', true, false)
							? {
									toggleableFieldSetIds: TOGGLEABLE_FIELD_SET_IDS,
									isInlineEditingEnabled: true,
									isTimelineHarmonisationM2Enabled: true,
								}
							: {
									isTimelineHarmonisationM2Enabled: false,
									isInlineEditingEnabled: false,
								}),
						shouldQuerySprints: true,
						sprintsActiveWithin: getSprintsActiveWithin(),
						shouldQuerySoftwareProjectSettings: true,
						shouldQueryLastAppliedFilters: true,
						shouldQueryReleases: true,
						shouldQueryBarColor: true,
						isTimelineGroupByEnabled: false,
						projectOrBoardInput: {
							boardId: Number(boardId),
						},
					},
				},
				filterRefinementQuery: {
					options: {
						fetchPolicy: fg('jira_software_timeline_perf_improvements')
							? ('store-or-network' as const)
							: ('store-and-network' as const),
					},
					parameters: filterRefinementParameters,
					variables: {
						cloudId,
						filterRefinementScope: {
							backlog: {
								boardId: Number(boardId),
							},
						},
						boardId: sourceARI,
						includeBoard: true,
					},
				},
			},
			...(fg('jira_software_timeline_perf_improvements')
				? {
						entryPoints: {
							nativeIssueTableEntryPoint: {
								entryPoint: nativeIssueTableEntryPoint,
								entryPointParams,
							},
						},
					}
				: {}),
		};
	},
});
